import {
    AppSettingsState,
    SymbologyValues,
} from 'app/store/appSettings/AppSettingsState';
import { PortfolioReturnsStreamRequest as CommsStreamRequest } from 'communication/types/portfolioReturns/PortfolioReturnsStreamRequest';
import { DateTime } from 'luxon';
import { PortfolioItem } from 'portfolio/PortfolioItem';
import State from 'store/State';
import { buildPortfolioReturnsConfigSlice } from 'widgets/portfolioReturns/store/configuration/configSlice';
import { buildPortfolioReturnsComponentSlice } from 'widgets/portfolioReturns/store/component/componentSlice';
import { filterPortfolio } from '../../averageExecPriceBenchmark';

const {
    selectors: { selectConfigState },
} = buildPortfolioReturnsConfigSlice();

const {
    selectors: { selectComponentState },
} = buildPortfolioReturnsComponentSlice();

export interface SymbolQuantity {
    symbol: string;
    quantity: number;
}

export interface PortfolioReturnsStreamRequest {
    /*
     * Buy quantities
     */
    buyQuantities: SymbolQuantity[];
    /*
     * Sell quantities
     * When global combineSellsAndShorts set, = sell + ss
     * When global combineSellsAndShorts not set, = sell
     */
    sellQuantities: SymbolQuantity[];
    /*
     * Short quantities
     * When global combineSellsAndShorts set, empty
     * When global combineSellsAndShorts not set, = ss
     */
    shortQuantities: SymbolQuantity[];
    symbology: SymbologyValues;
    days?: number;
    fromDate?: Date;
    toDate?: Date;
    index1: string;
    index2: string;
    indexesSymbology: SymbologyValues;
}

export interface BuildRequestOptions {
    combineSellsAndShorts: boolean;
    symbology: SymbologyValues;
    days?: number;
    fromDate?: Date;
    toDate?: Date;
    index1?: string;
    index2?: string;
    useAvgExecPrice: boolean;
}

export interface PortfolioReturnsTotal {
    buy: number;
    sell: number;
    short: number;
}
export type PortfolioReturnsTotals = { [key: string]: PortfolioReturnsTotal };

const initialTotal: PortfolioReturnsTotal = {
    buy: 0,
    sell: 0,
    short: 0,
};

export function buildRequest(
    options: BuildRequestOptions,
    positions: PortfolioItem[]
): PortfolioReturnsStreamRequest {
    const ret: PortfolioReturnsStreamRequest = {
        buyQuantities: [],
        sellQuantities: [],
        shortQuantities: [],
        symbology: options.symbology,
        days: options.days,
        fromDate: options.fromDate,
        toDate: options.toDate,
        indexesSymbology: options.symbology,
        index1: options.index1 ?? '',
        index2: options.index2 ?? '',
    };

    const useTradedQuantities = options.useAvgExecPrice;

    // eslint-disable-next-line prefer-const
    let { buyQuantities, sellQuantities, shortQuantities, totals } = aggregate(
        positions,
        ret,
        'symbol',
        options.combineSellsAndShorts,
        useTradedQuantities
    );

    const keys = Object.keys(totals);
    if (keys.length === 1 && keys[0] === '') {
        // We don't have any symbols sp try the Instinet symbols...
        const {
            buyQuantities: buyQuantities2,
            sellQuantities: sellQuantities2,
            shortQuantities: shortQuantities2,
        } = aggregate(
            positions,
            ret,
            'instinetSymbol',
            options.combineSellsAndShorts,
            useTradedQuantities
        );
        buyQuantities = buyQuantities2;
        sellQuantities = sellQuantities2;
        shortQuantities = shortQuantities2;
        ret.symbology = SymbologyValues.Instinet;
    }

    ret.buyQuantities = buyQuantities;
    ret.sellQuantities = sellQuantities;
    ret.shortQuantities = shortQuantities;

    return ret;
}

function aggregate(
    positions: PortfolioItem[],
    ret: PortfolioReturnsStreamRequest,
    symbolKey: 'instinetSymbol' | 'symbol',
    combineSellsAndShorts: boolean,
    useTradedQuantities: boolean
): {
    totals: PortfolioReturnsTotals;
    buyQuantities: SymbolQuantity[];
    sellQuantities: SymbolQuantity[];
    shortQuantities: SymbolQuantity[];
} {
    const totals = positions.reduce((t: PortfolioReturnsTotals, p) => {
        const symbol = p[symbolKey] ?? '';
        if (!t[symbol]) {
            t[symbol] = { ...initialTotal };
        }

        reduceTotals(t, p, combineSellsAndShorts, symbol, useTradedQuantities);

        return t;
    }, {});

    return {
        totals,
        buyQuantities: mapQuantities(totals, 'buy'),
        sellQuantities: mapQuantities(totals, 'sell'),
        shortQuantities: combineSellsAndShorts
            ? []
            : mapQuantities(totals, 'short'),
    };
}

export function mapPortfolioReturnsStreamRequestToSymQty(
    request: PortfolioReturnsStreamRequest
): string {
    const mapCollection = (c: SymbolQuantity[]) =>
        getNonZeroQuantities(c)
            .map((t) => `${t.symbol},${t.quantity}`)
            .join('|');

    const buy = mapCollection(request.buyQuantities);
    const sell = mapCollection(request.sellQuantities);
    const short = mapCollection(request.shortQuantities);

    const mapIndex = (i: string) => (i === '' ? '' : i + ',1');
    const index1 = mapIndex(request.index1);
    const index2 = mapIndex(request.index2);

    return `\`${buy}\`${sell}\`${short}\`${index1}\`${index2}`;
}

export function getNonZeroQuantities(
    quantities: SymbolQuantity[]
): SymbolQuantity[] {
    return quantities.filter(
        (sq) => sq.quantity !== undefined && sq.quantity !== 0
    );
}

export function mapPortfolioReturnsStreamRequestToSymbology(
    request: PortfolioReturnsStreamRequest
): string {
    // First 4 are for the totals based on the portfolio, the users symbology or Instinet if they were all blank
    // Last 2 are for the user entered indexes, so we use the users symbology
    return `${request.symbology}\`${request.symbology}\`${request.symbology}\`${request.symbology}\`${request.indexesSymbology}\`${request.indexesSymbology}`;
}

export function mapToCommsStreamRequest(
    request: PortfolioReturnsStreamRequest
): CommsStreamRequest {
    const formatDate = (d?: Date) =>
        d ? DateTime.fromJSDate(d).toFormat('yyyy-MMM-dd').toUpperCase() : null;
    return {
        symQty: mapPortfolioReturnsStreamRequestToSymQty(request),
        symbology: mapPortfolioReturnsStreamRequestToSymbology(request),
        days: request.days ?? null,
        toDate:
            request.fromDate && request.days
                ? null
                : formatDate(request.toDate),
        fromDate: formatDate(request.fromDate),
        barSize: 0,
    };
}

export function getExpectedValueTypes(request: PortfolioReturnsStreamRequest) {
    return {
        buys: getNonZeroQuantities(request.buyQuantities).length > 0,
        sells: getNonZeroQuantities(request.sellQuantities).length > 0,
        shorts: getNonZeroQuantities(request.shortQuantities).length > 0,
    };
}

export interface PortfolioReturnsRequestDependencies {
    days: number;
    chartDate?: Date;
    index1?: string;
    index2?: string;
}

export function createPortfolioReturnsStreamRequestPayload(
    widgetKey: string,
    appSettings: AppSettingsState,
    filteredPortfolio: PortfolioItem[],
    state: State
): CommsStreamRequest | undefined {
    const { combineSellsAndShorts, symbology } = appSettings;

    const config = selectConfigState(state, widgetKey);

    const { days, index1, index2, useAvgExecPrice } = config;
    const { fromDate, toDate: compToDate } = selectComponentState(
        state,
        widgetKey
    );

    const toDate = compToDate ? compToDate : fromDate ? undefined : new Date();

    const portfolio = filterPortfolio(filteredPortfolio, config);

    const request = buildRequest(
        {
            combineSellsAndShorts,
            symbology,
            days,
            fromDate,
            toDate,
            index1,
            index2,
            useAvgExecPrice: !!useAvgExecPrice,
        },
        portfolio
    );

    const expectedValueTypes = getExpectedValueTypes(request);

    return expectedValueTypes.buys ||
        expectedValueTypes.sells ||
        expectedValueTypes.shorts
        ? mapToCommsStreamRequest(request)
        : undefined;
}

function mapQuantities(
    totals: PortfolioReturnsTotals,
    k: keyof PortfolioReturnsTotal
): SymbolQuantity[] {
    const val = (t: PortfolioReturnsTotal) => t[k];
    return Object.keys(totals)
        .filter((symbol) => val(totals[symbol]) !== undefined)
        .map((symbol) => ({
            symbol,
            quantity: val(totals[symbol]),
        }));
}

function reduceTotals(
    t: PortfolioReturnsTotals,
    p: PortfolioItem,
    combineSellsAndShorts: boolean,
    symbol: string,
    useTradedQuantities: boolean
): void {
    const qty = useTradedQuantities ? p.qtyTraded : p.qty;
    t[symbol].buy = t[symbol].buy + (p.side === 'B' ? qty : 0);
    t[symbol].sell =
        t[symbol].sell +
        (p.side === 'S' || (p.side === 'SS' && combineSellsAndShorts)
            ? qty
            : 0);
    t[symbol].short =
        t[symbol].short + (p.side === 'SS' && !combineSellsAndShorts ? qty : 0);
}
